import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { getChannelById, updateChannelName } from "../../services/channelService";
import { getChatbotEnabledPublic } from "../../services/automationService";
import { 
  Loader2, 
  Settings, 
  MessageCircle, 
  Phone, 
  Calendar, 
  User,
  Save, 
  Edit3, 
  X,
  CheckCircle2,
  Workflow,
  ArrowLeft
} from "lucide-react";
import { useState } from "react";
import { useUserSession } from "../../hooks/useUserSession";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

type Tab = 'settings';

export default function Channel() {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const channelId = params.channelId;

  const userSession = useUserSession()
  
  const queryClient = useQueryClient();
  
  const [activeTab, setActiveTab] = useState<Tab>('settings');
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ["channel", channelId],
    queryFn: () => getChannelById(Number(channelId)),
    enabled: !!channelId
  });

  const { data: chatbotEnabledData } = useQuery({
    queryKey: ["chatbot-enabled-public", channelId],
    queryFn: () => getChatbotEnabledPublic(channelId!),
    enabled: !!channelId,
  });
  const chatbotEnabled = chatbotEnabledData?.chatbotEnabled !== false;

  const updateNameMutation = useMutation({
    mutationFn: updateChannelName,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["channel", channelId] });
      toast.success(data)
      setIsEditing(false);
      setEditedName('');
    }
  });

  const handleEditStart = () => {
    setEditedName(data?.nameChannel || '');
    setIsEditing(true);
  };

  const handleSave = () => {
    if (editedName.trim() && editedName !== data?.nameChannel) {
      updateNameMutation.mutate({channelId: Number(channelId), nameChannel: editedName.trim()});
    } else {
      setIsEditing(false);
      setEditedName('');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedName('');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading || userSession.isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-green-500 mx-auto mb-4" />
          <p className="text-lg text-gray-700">{t('loadingChannel')}</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-red-50 to-orange-50">
        <div className="text-center">
          <X className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-lg text-gray-700">{t('errorLoadingChannel')}</p>
        </div>
      </div>
    );
  }

  if(data.userId !== userSession.data?.id) {
    navigate('/');
  } 

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 mb-8 overflow-hidden">
          <div className="bg-gradient-to-r from-green-500 to-green-600 px-8 py-6">
            <div className="flex items-center space-x-4">
              <div className="bg-white/20 p-3 rounded-full">
                <MessageCircle className="h-8 w-8 text-white" />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-white mb-1">
                  {data.nameChannel}
                </h1>
                <p className="text-green-100 flex items-center">
                  <Phone className="h-4 w-4 mr-2" />
                  {data.phoneChannel}
                </p>
              </div>
              <div className="text-right text-green-100">
                <p className="text-sm">{t('channelId')}</p>
                <p className="text-lg font-semibold">#{data.id}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="border-b border-gray-200 flex items-center justify-between">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('settings')}
                className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors duration-200 ${
                  activeTab === 'settings'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Settings className="h-4 w-4" />
                  <span>{t('settings')}</span>
                </div>
              </button>
              <button
                onClick={() => chatbotEnabled && navigate(`/automation/${channelId}`)}
                disabled={!chatbotEnabled}
                title={!chatbotEnabled ? t('chatbotGloballyDisabledTitle') : undefined}
                className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors duration-200 ${
                  !chatbotEnabled
                    ? 'border-transparent text-gray-300 cursor-not-allowed'
                    : // window.location.pathname.includes('/automation/') && window.location.pathname.endsWith(channelId)
                      window.location.pathname.includes('/automation/') && channelId && window.location.pathname.endsWith(channelId.toString())
                      ? 'border-green-500 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Workflow className="h-4 w-4" />
                  <span>{t('automation')}</span>
                </div>
              </button>
            </nav>
            <button
              onClick={() => navigate(`/`)}
              className="flex items-center gap-1 text-gray-500 hover:text-green-600 px-3 py-2 text-sm font-medium"
              title={t('back')}
            >
              <ArrowLeft className="h-4 w-4" /> {t('back')}
            </button>
          </div>

          {/* Settings Content */}
          {activeTab === 'settings' && (
            <div className="p-8">
              <div className="space-y-8">
                {/* Channel Name Section */}
                <div className="bg-gray-50 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {t('channelName')}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {t('customizeChannelName')}
                      </p>
                    </div>
                    {!isEditing && (
                      <button
                        onClick={handleEditStart}
                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-green-600 bg-green-100 rounded-lg hover:bg-green-200 transition-colors duration-200"
                      >
                        <Edit3 className="h-4 w-4 mr-1" />
                        {t('edit')}
                      </button>
                    )}
                  </div>
                  {isEditing ? (
                    <div className="space-y-4">
                      <input
                        type="text"
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                        placeholder={t('channelNamePlaceholder')}
                        autoFocus
                      />
                      <div className="flex space-x-3">
                        <button
                          onClick={handleSave}
                          disabled={updateNameMutation.isPending || !editedName.trim()}
                          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                        >
                          {updateNameMutation.isPending ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Save className="h-4 w-4 mr-2" />
                          )} 
                          {t('save')}
                        </button>
                        <button
                          onClick={handleCancel}
                          disabled={updateNameMutation.isPending}
                          className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition-colors duration-200"
                        >
                          <X className="h-4 w-4 mr-2" />
                          {t('cancel')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <p className="text-lg font-medium text-gray-900">
                        {data.nameChannel}
                      </p>
                    </div>
                  )}
                </div>

                {/* Channel Information */}
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {t('channelInformation')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center mb-2">
                        <User className="h-5 w-5 text-gray-400 mr-3" />
                        {/* Información de usuario */}
                      </div>
                      <p className="text-gray-900 font-mono"></p>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      {/* Otra información */}
                    </div>
                    {data.createdAt && (
                      <div className="bg-white rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center mb-2">
                          <Calendar className="h-5 w-5 text-gray-400 mr-3" />
                          <span className="text-sm font-medium text-gray-500">{t('created')}</span>
                        </div>
                        <p className="text-gray-900">{formatDate(data.createdAt || '')}</p>
                      </div>
                    )}
                    {data.updatedAt && (
                      <div className="bg-white rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center mb-2">
                          <CheckCircle2 className="h-5 w-5 text-gray-400 mr-3" />
                          <span className="text-sm font-medium text-gray-500">{t('updated')}</span>
                        </div>
                        <p className="text-gray-900">{formatDate(data.updatedAt || '')}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}